import { useState, useEffect } from "react";
import axios from "axios";
import { User, Lock, Building2, Shield, Plus, Trash2, Edit, Camera, Save, Users, CheckCircle, XCircle, Eye, Loader2, Globe, Mail, Share2, Copy, Upload, MessageCircle, Image, Link2, ToggleLeft, Facebook, Instagram, Linkedin, Twitter, Megaphone, Send, Info, AlertTriangle, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import ImageCropperModal from "../../components/ImageCropperModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_PERMISSIONS = [
  { id: "packages", label: "Manage Packages" },
  { id: "inquiries", label: "Manage Inquiries" },
  { id: "agents", label: "Manage Agents" },
  { id: "quotes", label: "Manage Quotes" },
  { id: "bookings", label: "Manage Bookings" },
  { id: "payments", label: "Manage Payments" },
  { id: "reports", label: "View Reports" },
];


const AdminSettings = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Agents state
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]); // unfiltered, used for tab counts
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentStatusFilter, setAgentStatusFilter] = useState("all");
  // Create / Edit agent modal
  const [showAgentFormModal, setShowAgentFormModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);  // null = create mode
  const [agentForm, setAgentForm] = useState({
    name: "", email: "", company_name: "", phone: "",
    gstin: "", pan_number: "", address: "", city: "", state: "",
  });
  const [agentFormSaving, setAgentFormSaving] = useState(false);

  // Forms
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "employee",
    designation: "Employee",
    status: "active",
    permissions: []
  });

  const [profileForm, setProfileForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    district: "",
    state: "",
    pin_code: "",
    website: "",
    gst_number: "",
    pan_number: "",
    logo_url: "",
    profile_picture_url: "",
    designation: "",
    gst_verified: false,
    pan_verified: false,
    social_links: []
  });

  const [verifyingGst, setVerifyingGst] = useState(false);
  const [verifyingPan, setVerifyingPan] = useState(false);
  const [gstResult, setGstResult] = useState(null);
  const [panResult, setPanResult] = useState(null);

  // Invite state
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSending, setInviteSending] = useState(false);

  // Banner state
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerLink, setBannerLink] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [annSubmitting, setAnnSubmitting] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", message: "", priority: "info" });
  const annPriorityConfig = {
    info: { label: "Info", color: "bg-blue-100 text-blue-700", icon: Info, border: "border-blue-200" },
    important: { label: "Important", color: "bg-amber-100 text-amber-700", icon: AlertTriangle, border: "border-amber-200" },
    urgent: { label: "Urgent", color: "bg-red-100 text-red-700", icon: AlertCircle, border: "border-red-200" }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchEmployees();
      fetchAgents();
      fetchInvites();
      fetchBanners();
      fetchAnnouncements();
    }
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAgents();
    }
  }, [agentStatusFilter]);


  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(res.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileForm({ ...res.data, social_links: res.data.social_links || [] });
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  // Agent management functions
  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      // Always fetch the full list once; sort newest registered first; derive
      // status counts client-side so tab badges always reflect reality.
      const response = await axios.get(`${API}/admin/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = Array.isArray(response.data) ? response.data : [];
      const sorted = [...all].sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
      setAllAgents(sorted);
      const filtered = agentStatusFilter === "all"
        ? sorted
        : sorted.filter(a => (a.status || "").toLowerCase() === agentStatusFilter);
      setAgents(filtered);
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setAgentsLoading(false);
    }
  };

  const approveAgent = async (id) => {
    try {
      await axios.put(`${API}/admin/agents/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent approved!");
      fetchAgents();
      setSelectedAgent(null);
    } catch (error) {
      toast.error("Failed to approve agent");
    }
  };

  const blockAgent = async (id) => {
    try {
      await axios.put(`${API}/admin/agents/${id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent blocked!");
      fetchAgents();
      setSelectedAgent(null);
    } catch (error) {
      toast.error("Failed to block agent");
    }
  };

  const openCreateAgent = () => {
    setEditingAgent(null);
    setAgentForm({
      name: "", email: "", company_name: "", phone: "",
      gstin: "", pan_number: "", address: "", city: "", state: "",
    });
    setShowAgentFormModal(true);
  };

  const openEditAgent = (agent) => {
    setEditingAgent(agent);
    setAgentForm({
      name: agent.name || "",
      email: agent.email || "",
      company_name: agent.company_name || "",
      phone: agent.phone || "",
      gstin: agent.gstin || agent.gst_number || "",
      pan_number: agent.pan_number || "",
      address: agent.address || "",
      city: agent.city || "",
      state: agent.state || "",
    });
    setShowAgentFormModal(true);
  };

  const saveAgent = async () => {
    if (!agentForm.name.trim()) { toast.error("Name is required"); return; }
    if (!agentForm.email.trim()) { toast.error("Email is required"); return; }
    setAgentFormSaving(true);
    try {
      if (editingAgent) {
        await axios.put(`${API}/admin/agents/${editingAgent.id}`, agentForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agent updated");
      } else {
        await axios.post(`${API}/admin/agents`, agentForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agent created and auto-approved");
      }
      setShowAgentFormModal(false);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agent");
    } finally {
      setAgentFormSaving(false);
    }
  };

  // Invite functions
  const fetchInvites = async () => {
    try {
      setInvitesLoading(true);
      const res = await axios.get(`${API}/admin/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(res.data);
    } catch (error) {
      console.error("Error fetching invites:", error);
    } finally {
      setInvitesLoading(false);
    }
  };

  const sendEmailInvites = async () => {
    const emailList = inviteEmails.split(/[,\n;]+/).map(e => e.trim()).filter(e => e && e.includes("@"));
    if (emailList.length === 0) return toast.error("Enter at least one valid email");
    if (emailList.length > 100) return toast.error("Maximum 100 emails per invite");
    try {
      setInviteSending(true);
      await axios.post(`${API}/admin/invites`, {
        emails: emailList,
        message: inviteMessage,
        method: "email"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invite sent to ${emailList.length} email(s)`);
      setInviteEmails("");
      setInviteMessage("");
      fetchInvites();
    } catch (error) {
      toast.error("Failed to send invites");
    } finally {
      setInviteSending(false);
    }
  };

  const createWhatsAppInvite = async () => {
    try {
      setInviteSending(true);
      const res = await axios.post(`${API}/admin/invites`, {
        emails: [],
        message: inviteMessage,
        method: "whatsapp"
      }, { headers: { Authorization: `Bearer ${token}` } });
      const code = res.data.invite?.code;
      const text = encodeURIComponent(`You're invited to join Happy Holidays DMC!\n\nInvite Code: ${code}\n${inviteMessage ? `\n${inviteMessage}\n` : ""}\nRegister at our platform to get started.`);
      window.open(`https://wa.me/?text=${text}`, "_blank");
      toast.success("WhatsApp invite created!");
      setInviteMessage("");
      fetchInvites();
    } catch (error) {
      toast.error("Failed to create invite");
    } finally {
      setInviteSending(false);
    }
  };

  const deleteInvite = async (id) => {
    try {
      await axios.delete(`${API}/admin/invites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invite deleted");
      fetchInvites();
    } catch (error) {
      toast.error("Failed to delete invite");
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const emails = text.split(/[,\n;\r]+/).map(e => e.trim()).filter(e => e && e.includes("@"));
      setInviteEmails(prev => prev ? prev + "\n" + emails.join("\n") : emails.join("\n"));
      toast.success(`${emails.length} email(s) imported from CSV`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Banner functions
  const fetchBanners = async () => {
    try {
      setBannersLoading(true);
      const res = await axios.get(`${API}/admin/banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(res.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setBannersLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      setAnnouncementsLoading(true);
      const res = await axios.get(`${API}/announcements/all`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(res.data);
    } catch (err) { console.error("Announcements fetch error:", err); }
    finally { setAnnouncementsLoading(false); }
  };

  const handleAnnSubmit = async (e) => {
    e.preventDefault();
    if (!annForm.title.trim() || !annForm.message.trim()) { toast.error("Title and message are required"); return; }
    try {
      setAnnSubmitting(true);
      await axios.post(`${API}/announcements`, annForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Announcement published & emailed to all active agents!");
      setAnnForm({ title: "", message: "", priority: "info" });
      setShowAnnForm(false);
      fetchAnnouncements();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create announcement"); }
    finally { setAnnSubmitting(false); }
  };

  const handleAnnDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/announcements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch { toast.error("Failed to delete"); }
  };

  const uploadBanner = async () => {
    if (!bannerFile) return toast.error("Select an image file");
    if (bannerFile.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    try {
      setBannerUploading(true);
      const formData = new FormData();
      formData.append("image", bannerFile);
      formData.append("title", bannerTitle);
      formData.append("link", bannerLink);
      formData.append("enabled", true);
      await axios.post(`${API}/admin/banners`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Banner uploaded!");
      setBannerTitle("");
      setBannerLink("");
      setBannerFile(null);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload banner");
    } finally {
      setBannerUploading(false);
    }
  };

  const toggleBanner = async (id, enabled) => {
    try {
      await axios.put(`${API}/admin/banners/${id}`, { enabled: !enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanners();
    } catch (error) {
      toast.error("Failed to update banner");
    }
  };

  const deleteBanner = async (id) => {
    try {
      await axios.delete(`${API}/admin/banners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Banner deleted");
      fetchBanners();
    } catch (error) {
      toast.error("Failed to delete banner");
    }
  };

  const getAgentStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-100 text-amber-700";
      case "approved": return "bg-emerald-100 text-emerald-700";
      case "blocked": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed!");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!employeeForm.name || !employeeForm.email) {
      toast.error("Name and Email are required");
      return;
    }
    if (!editingEmployee && !employeeForm.password) {
      toast.error("Password is required for new users");
      return;
    }

    try {
      if (editingEmployee) {
        await axios.put(`${API}/admin/employees/${editingEmployee.id}`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User updated!");
      } else {
        await axios.post(`${API}/admin/employees`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User added!");
      }
      setShowEmployeeModal(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/auth/me/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Without this, the logged-in AuthContext user stays stale (missing the just-saved
      // logo/address/mobile/photo/designation) until a hard page reload, which is why quote
      // PDFs downloaded right after a profile edit were missing this info in the header/footer.
      if (refreshUser) await refreshUser();
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const verifyGST = async () => {
    if (!profileForm.gst_number || profileForm.gst_number.length < 15) {
      toast.error("Please enter a valid 15-character GSTIN");
      return;
    }
    setVerifyingGst(true);
    setGstResult(null);
    try {
      const res = await axios.post(`${API}/verify/gstin`, { gstin: profileForm.gst_number }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGstResult(res.data);
      if (res.data.valid) {
        setProfileForm(p => ({ ...p, gst_verified: true }));
        toast.success(`GST Verified! State: ${res.data.state_name}`);
      } else {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setVerifyingGst(false); }
  };

  const verifyPAN = async () => {
    if (!profileForm.pan_number || profileForm.pan_number.length < 10) {
      toast.error("Please enter a valid 10-character PAN");
      return;
    }
    setVerifyingPan(true);
    setPanResult(null);
    try {
      const res = await axios.post(`${API}/verify/pan`, { pan: profileForm.pan_number }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPanResult(res.data);
      if (res.data.valid) {
        setProfileForm(p => ({ ...p, pan_verified: true }));
        toast.success(`PAN Verified! Type: ${res.data.holder_type}`);
      } else {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setVerifyingPan(false); }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted!");
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const resetEmployeeForm = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      name: "",
      email: "",
      password: "",
      phone: "",
      role: "employee",
      designation: "Employee",
      status: "active",
      permissions: []
    });
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      email: emp.email,
      password: "", 
      phone: emp.phone || "",
      role: emp.role,
      designation: emp.designation || "Employee",
      status: emp.status || "active",
      permissions: emp.permissions || []
    });
    setShowEmployeeModal(true);
  };

  const togglePermission = (permId) => {
    setEmployeeForm(prev => {
      if (prev.permissions.includes(permId)) {
        return { ...prev, permissions: prev.permissions.filter(p => p !== permId) };
      } else {
        return { ...prev, permissions: [...prev.permissions, permId] };
      }
    });
  };

  const [cropper, setCropper] = useState({ open: false, file: null });

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size should be less than 5MB"); e.target.value = ""; return; }
    setCropper({ open: true, file });
    e.target.value = "";
  };

  const handleCroppedLogoSave = (dataUrl) => {
    setProfileForm(prev => ({ ...prev, logo_url: dataUrl }));
    setCropper({ open: false, file: null });
    toast.success("Logo updated! Click 'Save Profile' to persist.");
  };

  const [profilePicCropper, setProfilePicCropper] = useState({ open: false, file: null });

  const handleProfilePicUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size should be less than 5MB"); e.target.value = ""; return; }
    setProfilePicCropper({ open: true, file });
    e.target.value = "";
  };

  const handleCroppedProfilePicSave = (dataUrl) => {
    setProfileForm(prev => ({ ...prev, profile_picture_url: dataUrl }));
    setProfilePicCropper({ open: false, file: null });
    toast.success("Profile picture updated! Click 'Save Profile' to persist.");
  };


  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-settings">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
          <p className="text-gray-500">Manage account, security and team access</p>
        </div>

        <Tabs defaultValue="agents" className="space-y-6">
          <TabsList className="bg-gray-50 flex-wrap h-auto">
            {user?.role === 'admin' && (
              <TabsTrigger value="agents" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Agents
              </TabsTrigger>
            )}
            {user?.role === 'admin' && (
              <TabsTrigger value="invites" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Mail className="w-4 h-4 mr-2" /> Invite Agents
              </TabsTrigger>
            )}
            {user?.role === 'admin' && (
              <TabsTrigger value="banners" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Image className="w-4 h-4 mr-2" /> Ad Banners
              </TabsTrigger>
            )}
            {user?.role === 'admin' && (
              <TabsTrigger value="announcements" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Megaphone className="w-4 h-4 mr-2" /> Announcements
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" /> My Profile
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="users" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Shield className="w-4 h-4 mr-2" /> Users & Access
              </TabsTrigger>
            )}
          </TabsList>

          {/* Users Tab */}
          {user?.role === 'admin' && (
            <TabsContent value="users">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-800">Team Management</CardTitle>
                  <Button onClick={() => { resetEmployeeForm(); setShowEmployeeModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" /> Add User
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm text-gray-500">User</th>
                          <th className="px-4 py-3 text-left text-sm text-gray-500">Role & Designation</th>
                          <th className="px-4 py-3 text-left text-sm text-gray-500">Status</th>
                          <th className="px-4 py-3 text-left text-sm text-gray-500">Permissions</th>
                          <th className="px-4 py-3 text-right text-sm text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {employees.map(emp => (
                          <tr key={emp.id} className="hover:bg-gray-50/30">
                            <td className="px-4 py-3">
                              <p className="text-gray-800 font-medium">{emp.name}</p>
                              <p className="text-xs text-gray-400">{emp.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-bold mr-2 ${emp.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {emp.role.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-500">{emp.designation || 'Employee'}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${emp.status === 'active' || emp.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {emp.status === 'approved' ? 'Active' : emp.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {emp.role === 'admin' ? (
                                  <span className="text-xs text-green-400">Full Access</span>
                                ) : (
                                  emp.permissions?.map(p => (
                                    <span key={p} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                                      {p}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => openEditEmployee(emp)}>
                                  <Edit className="w-4 h-4 text-gray-500" />
                                </Button>
                                {emp.id !== user.id && (
                                  <Button variant="ghost" size="sm" onClick={() => deleteEmployee(emp.id)}>
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Agents Tab */}
          {user?.role === 'admin' && (
            <TabsContent value="agents">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-gray-800">Manage Agents</CardTitle>
                  <Button onClick={openCreateAgent} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-agent-btn">
                    <Plus className="w-4 h-4 mr-1" /> Create Agent
                  </Button>
                </CardHeader>

                {/* Status tabs with live counts (newest registered first) */}
                <div className="px-6 pb-2">
                  <div className="flex flex-wrap gap-2" data-testid="agent-status-tabs">
                    {[
                      { id: "all",      label: "Total Agents",  count: allAgents.length,                                                      activeCls: "bg-slate-900 text-white border-slate-900" },
                      { id: "approved", label: "Approved",      count: allAgents.filter(a => (a.status || "").toLowerCase() === "approved").length, activeCls: "bg-emerald-600 text-white border-emerald-600" },
                      { id: "pending",  label: "Pending",       count: allAgents.filter(a => (a.status || "").toLowerCase() === "pending").length,  activeCls: "bg-amber-500 text-white border-amber-500" },
                      { id: "blocked",  label: "Blocked",       count: allAgents.filter(a => (a.status || "").toLowerCase() === "blocked").length,  activeCls: "bg-rose-600 text-white border-rose-600" },
                    ].map(tab => {
                      const active = agentStatusFilter === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setAgentStatusFilter(tab.id)}
                          className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${active ? tab.activeCls + " shadow-sm" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"}`}
                          data-testid={`agent-tab-${tab.id}`}
                        >
                          <span>{tab.label}</span>
                          <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${active ? "bg-white/25 text-white" : "bg-gray-200 text-gray-700"}`}>
                            {tab.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <CardContent className="p-0">
                  {agentsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                    </div>
                  ) : agents.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-gray-500">No agents found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Agent</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Company</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Contact</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Location</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Last Login</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {agents.map((agent) => (
                            <tr key={agent.id} className="hover:bg-gray-50/30">
                              <td className="px-6 py-4">
                                <p className="text-gray-800 font-medium" style={{textTransform: "capitalize"}}>{agent.name?.toLowerCase()}</p>
                                <p className="text-gray-400 text-sm">{agent.email?.toLowerCase()}</p>
                              </td>
                              <td className="px-6 py-4 text-gray-600" style={{textTransform: "capitalize"}}>
                                {agent.company_name?.toLowerCase() || "-"}
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {agent.phone || "-"}
                              </td>
                              <td className="px-6 py-4 text-gray-500 text-sm">
                                {agent.city && agent.state ? `${agent.city}, ${agent.state}` : "-"}
                              </td>
                              <td className="px-6 py-4">
                                <Badge className={getAgentStatusColor(agent.status)}>
                                  {agent.status?.charAt(0).toUpperCase() + agent.status?.slice(1)}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 text-gray-500 text-sm">
                                {agent.last_login ? (() => { const d = new Date(agent.last_login); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : <span className="text-gray-300">Never</span>}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => setSelectedAgent(agent)}
                                    className="text-gray-500 hover:text-gray-900"
                                    data-testid={`view-agent-${agent.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => openEditAgent(agent)}
                                    className="text-blue-500 hover:text-blue-700"
                                    data-testid={`edit-agent-${agent.id}`}
                                    title="Edit agent"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {agent.status === "pending" && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => approveAgent(agent.id)}
                                      className="text-green-400 hover:text-green-300"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {agent.status !== "blocked" && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => blockAgent(agent.id)}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Invite Agents Tab */}
          {user?.role === 'admin' && (
            <TabsContent value="invites">
              <div className="space-y-6">
                {/* Send Invite Emails Card */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-emerald-600" /> Send Invite Emails
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {/* Custom Message First */}
                    <div>
                      <Label className="text-gray-700 font-medium">Custom Message (optional)</Label>
                      <textarea
                        data-testid="invite-message-input"
                        className="w-full min-h-[80px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-y mt-1.5"
                        placeholder="We'd love to have you as our travel partner..."
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                      />
                    </div>
                    {/* Email Addresses */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="text-gray-700 font-medium">Email Addresses (1-100)</Label>
                        <label className="cursor-pointer">
                          <input type="file" accept=".csv,.txt" className="hidden" onChange={handleCsvUpload} />
                          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium hover:underline transition-colors">
                            <Upload className="w-3.5 h-3.5" /> Upload CSV
                          </span>
                        </label>
                      </div>
                      <textarea
                        data-testid="invite-emails-input"
                        className="w-full min-h-[110px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-y"
                        placeholder={"Enter emails separated by comma, newline, or semicolon:\nagent1@company.com\nagent2@company.com, agent3@company.com"}
                        value={inviteEmails}
                        onChange={(e) => setInviteEmails(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1.5">Paste emails or upload CSV</p>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-1">
                      <Button
                        data-testid="send-email-invite-btn"
                        onClick={sendEmailInvites}
                        disabled={inviteSending || !inviteEmails.trim()}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 h-11 text-sm font-medium"
                      >
                        {inviteSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                        Send Email Invites
                      </Button>
                      <Button
                        data-testid="whatsapp-invite-btn"
                        onClick={createWhatsAppInvite}
                        disabled={inviteSending}
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-11 text-sm font-medium whitespace-nowrap"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" /> Share via WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Invite History Card */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-emerald-600" /> Invite History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {invitesLoading ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                    ) : invites.length === 0 ? (
                      <p className="text-center text-gray-400 py-8">No invites sent yet</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full" data-testid="invite-history-table">
                          <thead className="bg-gray-50/50">
                            <tr>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Invite Code</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Sent To</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                              <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {invites.map((inv) => (
                              <tr key={inv.id} className="hover:bg-gray-50/50">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <code className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-sm font-mono font-bold">{inv.code}</code>
                                    <button
                                      data-testid={`copy-code-${inv.code}`}
                                      onClick={() => copyToClipboard(inv.code)}
                                      className="text-gray-400 hover:text-emerald-600 transition-colors"
                                    >
                                      <Copy className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <Badge className={inv.method === "email" ? "bg-emerald-100 text-emerald-700 border-0 hover:bg-emerald-100" : "bg-green-100 text-green-700 border-0 hover:bg-green-100"}>
                                    {inv.method === "email" ? "Email" : "WhatsApp"}
                                  </Badge>
                                </td>
                                <td className="p-3 text-sm text-gray-600">
                                  {inv.emails?.length > 0 ? `${inv.emails.length} email(s)` : "—"}
                                </td>
                                <td className="p-3 text-sm text-gray-500">
                                  {(() => { const d = new Date(inv.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
                                </td>
                                <td className="p-3 text-right">
                                  <Button
                                    data-testid={`delete-invite-${inv.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteInvite(inv.id)}
                                    className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Ad Banners Tab */}
          {user?.role === 'admin' && (
            <TabsContent value="banners">
              <div className="space-y-6">
                {/* Upload Banner Card */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      <Image className="w-5 h-5 text-emerald-600" /> Upload Banner
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Banner Image (up to 5MB)</Label>
                      <p className="text-xs text-gray-400 mb-1.5">Recommended size: 1920 x 500px (PNG/JPG)</p>
                      <input
                        type="file"
                        accept="image/*"
                        data-testid="banner-image-input"
                        onChange={(e) => setBannerFile(e.target.files[0])}
                        className="w-full mt-1.5 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                      />
                      {bannerFile && (
                        <p className="text-xs text-gray-400 mt-1">{bannerFile.name} ({(bannerFile.size / 1024).toFixed(0)} KB)</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700 font-medium">Title (optional)</Label>
                        <Input
                          data-testid="banner-title-input"
                          value={bannerTitle}
                          onChange={(e) => setBannerTitle(e.target.value)}
                          placeholder="e.g. Summer Sale 2026"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Click URL (optional)</Label>
                        <Input
                          data-testid="banner-link-input"
                          value={bannerLink}
                          onChange={(e) => setBannerLink(e.target.value)}
                          placeholder="https://..."
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                    <Button
                      data-testid="upload-banner-btn"
                      onClick={uploadBanner}
                      disabled={bannerUploading || !bannerFile}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {bannerUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Banner
                    </Button>
                  </CardContent>
                </Card>

                {/* Banner List Card */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-gray-800 flex items-center gap-2">
                        <ToggleLeft className="w-5 h-5 text-emerald-600" /> Banner List
                      </CardTitle>
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">
                        Active Banners {banners.filter(b => b.enabled).length}/{banners.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {bannersLoading ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                    ) : banners.length === 0 ? (
                      <p className="text-center text-gray-400 py-8">No banners uploaded yet</p>
                    ) : (
                      <div className="space-y-3">
                        {banners.map((banner) => (
                          <div key={banner.id} data-testid={`banner-item-${banner.id}`} className={`flex items-center gap-4 p-3 rounded-lg border ${banner.enabled ? "border-emerald-200 bg-emerald-50/30" : "border-gray-200 bg-gray-50/50 opacity-60"}`}>
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`}
                              alt={banner.title || "Banner"}
                              className="w-24 h-14 object-cover rounded-md border border-gray-200"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 text-sm truncate">{banner.title || "Untitled Banner"}</p>
                              {banner.link && (
                                <p className="text-xs text-gray-400 truncate flex items-center gap-1"><Link2 className="w-3 h-3" />{banner.link}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-0.5">{(() => { const d = new Date(banner.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={banner.enabled}
                                onCheckedChange={() => toggleBanner(banner.id, banner.enabled)}
                                data-testid={`toggle-banner-${banner.id}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteBanner(banner.id)}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                data-testid={`delete-banner-${banner.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Announcements Tab */}
          {user?.role === 'admin' && (
            <TabsContent value="announcements">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-emerald-600" /> Announcements
                  </CardTitle>
                  <Button onClick={() => setShowAnnForm(!showAnnForm)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="new-announcement-btn">
                    <Plus className="w-4 h-4" /> New Announcement
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-gray-500">Send announcements to all active agents. Auto-expires after 10 days.</p>

                  {/* Create Form */}
                  {showAnnForm && (
                    <div className="border border-emerald-200 rounded-xl p-5 bg-emerald-50/30 space-y-4" data-testid="announcement-form">
                      <form onSubmit={handleAnnSubmit} className="space-y-4">
                        <div>
                          <Label className="text-gray-700">Title</Label>
                          <Input value={annForm.title} onChange={e => setAnnForm({ ...annForm, title: e.target.value })} placeholder="Announcement title..." data-testid="announcement-title-input" />
                        </div>
                        <div>
                          <Label className="text-gray-700">Message</Label>
                          <textarea value={annForm.message} onChange={e => setAnnForm({ ...annForm, message: e.target.value })} placeholder="Write your announcement message..." rows={4} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y" data-testid="announcement-message-input" />
                        </div>
                        <div>
                          <Label className="text-gray-700 mb-2 block">Priority</Label>
                          <div className="flex gap-3" data-testid="priority-selector">
                            {Object.entries(annPriorityConfig).map(([key, cfg]) => {
                              const Icon = cfg.icon;
                              return (
                                <button key={key} type="button" onClick={() => setAnnForm({ ...annForm, priority: key })}
                                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${annForm.priority === key ? `${cfg.color} ${cfg.border} ring-2 ring-offset-1 ring-current` : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"}`}
                                  data-testid={`priority-${key}-btn`}>
                                  <Icon className="w-4 h-4" />{cfg.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <Button type="submit" disabled={annSubmitting} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="publish-announcement-btn">
                            <Send className="w-4 h-4" />{annSubmitting ? "Publishing..." : "Publish & Email Agents"}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowAnnForm(false)}>Cancel</Button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* List */}
                  {announcementsLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
                  ) : announcements.length === 0 ? (
                    <div className="py-10 text-center">
                      <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No announcements yet. Create the first one!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {announcements.map(a => {
                        const cfg = annPriorityConfig[a.priority] || annPriorityConfig.info;
                        const Icon = cfg.icon;
                        return (
                          <div key={a.id} className={`bg-white border rounded-xl p-4 flex items-start justify-between gap-4 ${a.is_expired ? "opacity-50" : ""}`} data-testid={`announcement-${a.id}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}><Icon className="w-3 h-3" />{cfg.label}</span>
                                {a.is_expired && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Expired</span>}
                              </div>
                              <p className="font-semibold text-gray-900">{a.title}</p>
                              <p className="text-gray-600 text-sm mt-0.5 whitespace-pre-line">{a.message}</p>
                              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />{a.created_at ? new Date(a.created_at).toLocaleString() : "-"} · Expires: {a.expires_at ? new Date(a.expires_at).toLocaleDateString() : "-"}
                              </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleAnnDelete(a.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-announcement-${a.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-gray-800">My Profile</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  {/* Logo Upload */}
                  <div className="flex items-center gap-6 mb-6">
                    <div className="relative group">
                      <Avatar className="w-24 h-24 border-4 border-gray-200">
                        <AvatarImage src={profileForm.logo_url} />
                        <AvatarFallback className="bg-gray-50 text-2xl font-bold">
                          {profileForm.company_name?.charAt(0) || "C"}
                        </AvatarFallback>
                      </Avatar>
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                        <Camera className="w-6 h-6 text-gray-800" />
                        <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                      </label>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-800">Company Logo</h3>
                      <p className="text-sm text-gray-500">Click image to upload new logo</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Displays at <strong>96 × 96 px</strong> on cards &amp; PDF header. Recommended upload: 512 × 512 px (square).</p>
                    </div>
                  </div>

                  {/* Admin Profile Picture Upload */}
                  <div className="flex items-center gap-6 mb-6" data-testid="admin-profile-picture-section">
                    <div className="relative group">
                      <Avatar className="w-24 h-24 border-4 border-emerald-100">
                        <AvatarImage src={profileForm.profile_picture_url} />
                        <AvatarFallback className="bg-emerald-50 text-2xl font-bold text-emerald-700">
                          {profileForm.name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || "A"}
                        </AvatarFallback>
                      </Avatar>
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity" data-testid="admin-profile-picture-upload-label">
                        <Camera className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={handleProfilePicUpload} data-testid="admin-profile-picture-input" />
                      </label>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-800">Admin Profile Picture</h3>
                      <p className="text-sm text-gray-500">Click image to upload your photo</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Appears in the <strong>Travel Advisor</strong> block on PDF & Word quotations. Recommended: 512 × 512 px (square).</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Admin Name</Label>
                      <Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={profileForm.email} disabled className="bg-gray-50/50 border-gray-200 text-gray-500" />
                    </div>
                    <div>
                      <Label>Company Name</Label>
                      <Input value={profileForm.company_name} onChange={e => setProfileForm({...profileForm, company_name: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Input value={profileForm.designation || ""} onChange={e => setProfileForm({...profileForm, designation: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="e.g. Owner, Director, Travel Manager" data-testid="admin-designation-input" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Input value={profileForm.address} onChange={e => setProfileForm({...profileForm, address: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input value={profileForm.city} onChange={e => setProfileForm({...profileForm, city: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>District</Label>
                      <Input value={profileForm.district} onChange={e => setProfileForm({...profileForm, district: e.target.value})} className="bg-gray-50 border-gray-200" data-testid="admin-district-input" />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input value={profileForm.state} onChange={e => setProfileForm({...profileForm, state: e.target.value})} className="bg-gray-50 border-gray-200" />
                    </div>
                    <div>
                      <Label>Pin Code</Label>
                      <Input value={profileForm.pin_code} onChange={e => setProfileForm({...profileForm, pin_code: e.target.value})} className="bg-gray-50 border-gray-200" data-testid="admin-pincode-input" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input value={profileForm.website} onChange={e => setProfileForm({...profileForm, website: e.target.value})} className="bg-gray-50 border-gray-200 pl-9" placeholder="www.example.com" data-testid="admin-website-input" />
                      </div>
                    </div>
                    <div>
                      <Label>GST Number</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            value={profileForm.gst_number}
                            onChange={e => { setProfileForm({...profileForm, gst_number: e.target.value.toUpperCase(), gst_verified: false}); setGstResult(null); }}
                            className={`bg-gray-50 border-gray-200 uppercase font-mono ${profileForm.gst_verified ? 'border-emerald-400 pr-9' : ''}`}
                            maxLength={15}
                            placeholder="03BQJPS3148F2ZU"
                            data-testid="admin-gst-input"
                          />
                          {profileForm.gst_verified && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" data-testid="gst-verified-icon" />}
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={verifyGST} disabled={verifyingGst || !profileForm.gst_number} className="h-10 px-3 text-xs whitespace-nowrap" data-testid="verify-gst-btn">
                          {verifyingGst ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                      {gstResult && gstResult.valid && (
                        <p className="text-xs text-emerald-600 mt-1" data-testid="gst-verify-result">Verified - {gstResult.entity_type} | {gstResult.state_name}</p>
                      )}
                      {gstResult && !gstResult.valid && (
                        <p className="text-xs text-red-500 mt-1">{gstResult.error}</p>
                      )}
                    </div>
                    <div>
                      <Label>PAN Number</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            value={profileForm.pan_number}
                            onChange={e => { setProfileForm({...profileForm, pan_number: e.target.value.toUpperCase(), pan_verified: false}); setPanResult(null); }}
                            className={`bg-gray-50 border-gray-200 uppercase font-mono ${profileForm.pan_verified ? 'border-emerald-400 pr-9' : ''}`}
                            maxLength={10}
                            placeholder="ABCDE1234F"
                            data-testid="admin-pan-input"
                          />
                          {profileForm.pan_verified && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" data-testid="pan-verified-icon" />}
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={verifyPAN} disabled={verifyingPan || !profileForm.pan_number} className="h-10 px-3 text-xs whitespace-nowrap" data-testid="verify-pan-btn">
                          {verifyingPan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                      {panResult && panResult.valid && (
                        <p className="text-xs text-emerald-600 mt-1" data-testid="pan-verify-result">Verified - {panResult.holder_type}</p>
                      )}
                      {panResult && !panResult.valid && (
                        <p className="text-xs text-red-500 mt-1">{panResult.error}</p>
                      )}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="text-base font-semibold text-gray-800 flex items-center gap-2">
                          <Share2 className="w-4 h-4 text-emerald-600" /> Social Links
                        </Label>
                        <p className="text-[11px] text-gray-400 mt-0.5">Displayed on public Contact Us page</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setProfileForm({
                          ...profileForm,
                          social_links: [...(profileForm.social_links || []), { platform: "facebook", url: "" }]
                        })}
                        className="h-8 bg-emerald-600 hover:bg-emerald-700 gap-1"
                        data-testid="add-social-link-btn"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Link
                      </Button>
                    </div>

                    {(profileForm.social_links || []).length === 0 && (
                      <p className="text-sm text-gray-400 italic py-2">No social links added yet.</p>
                    )}

                    <div className="space-y-2">
                      {(profileForm.social_links || []).map((link, idx) => {
                        const platformIcons = {
                          facebook: <Facebook className="w-4 h-4 text-[#1877F2]" />,
                          instagram: <Instagram className="w-4 h-4 text-[#E4405F]" />,
                          linkedin: <Linkedin className="w-4 h-4 text-[#0A66C2]" />,
                          twitter: <Twitter className="w-4 h-4 text-[#1DA1F2]" />,
                        };
                        return (
                          <div key={idx} className="flex gap-2 items-center" data-testid={`social-link-${idx}`}>
                            <Select
                              value={link.platform}
                              onValueChange={v => {
                                const updated = [...profileForm.social_links];
                                updated[idx] = { ...updated[idx], platform: v };
                                setProfileForm({ ...profileForm, social_links: updated });
                              }}
                            >
                              <SelectTrigger className="w-40 bg-gray-50 border-gray-200" data-testid={`social-platform-${idx}`}>
                                <div className="flex items-center gap-2">
                                  {platformIcons[link.platform] || <Link2 className="w-4 h-4 text-gray-500" />}
                                  <SelectValue />
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="facebook"><span className="flex items-center gap-2"><Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook</span></SelectItem>
                                <SelectItem value="instagram"><span className="flex items-center gap-2"><Instagram className="w-4 h-4 text-[#E4405F]" /> Instagram</span></SelectItem>
                                <SelectItem value="linkedin"><span className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-[#0A66C2]" /> LinkedIn</span></SelectItem>
                                <SelectItem value="twitter"><span className="flex items-center gap-2"><Twitter className="w-4 h-4 text-[#1DA1F2]" /> Twitter / X</span></SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              value={link.url}
                              onChange={e => {
                                const updated = [...profileForm.social_links];
                                updated[idx] = { ...updated[idx], url: e.target.value };
                                setProfileForm({ ...profileForm, social_links: updated });
                              }}
                              placeholder="https://..."
                              className="flex-1 bg-gray-50 border-gray-200"
                              data-testid={`social-url-${idx}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = profileForm.social_links.filter((_, i) => i !== idx);
                                setProfileForm({ ...profileForm, social_links: updated });
                              }}
                              className="h-10 w-10 p-0 text-red-400 hover:text-red-600"
                              data-testid={`remove-social-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto">
                    <Save className="w-4 h-4 mr-2" /> Save Profile
                  </Button>
                </form>

                {/* App Preferences: Chat Toggle */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-emerald-600" /> App Preferences
                  </h4>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Chat Feature</p>
                      <p className="text-xs text-gray-500 mt-0.5">Toggle the Chat icon in the header. Turn off to hide it from view.</p>
                    </div>
                    <Switch
                      checked={localStorage.getItem('chat_feature_enabled') !== 'false'}
                      onCheckedChange={(checked) => {
                        localStorage.setItem('chat_feature_enabled', checked ? 'true' : 'false');
                        window.dispatchEvent(new Event('chat_pref_changed'));
                        toast.success(`Chat ${checked ? 'enabled' : 'disabled'}. Changes take effect immediately.`);
                      }}
                      data-testid="chat-toggle-switch"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab REMOVED as per user request */}
        </Tabs>

        {/* User Modal */}
        <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? "Edit User" : "Add New User"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEmployeeSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={employeeForm.name} onChange={e => setEmployeeForm({...employeeForm, name: e.target.value})} className="bg-gray-50 border-gray-200" required />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={employeeForm.email} onChange={e => setEmployeeForm({...employeeForm, email: e.target.value})} className="bg-gray-50 border-gray-200" required disabled={!!editingEmployee} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={employeeForm.phone} onChange={e => setEmployeeForm({...employeeForm, phone: e.target.value})} className="bg-gray-50 border-gray-200" />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input value={employeeForm.designation} onChange={e => setEmployeeForm({...employeeForm, designation: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="e.g. Sales Manager" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select value={employeeForm.role} onValueChange={v => setEmployeeForm({...employeeForm, role: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="status-mode" 
                      checked={employeeForm.status === 'active'}
                      onCheckedChange={(checked) => setEmployeeForm(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }))}
                    />
                    <Label htmlFor="status-mode" className={employeeForm.status === 'active' ? 'text-green-400' : 'text-gray-500'}>
                      {employeeForm.status === 'active' ? 'Active User' : 'Inactive'}
                    </Label>
                  </div>
                </div>
              </div>

              {!editingEmployee && (
                <div>
                  <Label>Password *</Label>
                  <Input type="password" value={employeeForm.password} onChange={e => setEmployeeForm({...employeeForm, password: e.target.value})} className="bg-gray-50 border-gray-200" required />
                </div>
              )}
              {editingEmployee && (
                <div>
                  <Label>New Password (Optional)</Label>
                  <Input type="password" value={employeeForm.password} onChange={e => setEmployeeForm({...employeeForm, password: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="Leave blank to keep current" />
                </div>
              )}

              {employeeForm.role === 'employee' && (
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <Label className="mb-2 block text-emerald-600">Access Permissions</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_PERMISSIONS.map(perm => (
                      <label key={perm.id} className="flex items-center space-x-2 cursor-pointer select-none" data-testid={`perm-${perm.id}`}>
                        <input 
                          type="checkbox"
                          checked={employeeForm.permissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="h-4 w-4 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500 accent-emerald-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Save User</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Agent Detail Dialog */}
        <Dialog open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
          <DialogContent className="max-w-lg bg-white border-gray-200 text-gray-800">
            {selectedAgent && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading">Agent Details</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-500 text-sm">Name</p>
                      <p className="font-medium" style={{textTransform: "capitalize"}}>{selectedAgent.name?.toLowerCase()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Status</p>
                      <Badge className={getAgentStatusColor(selectedAgent.status)}>
                        {selectedAgent.status?.charAt(0).toUpperCase() + selectedAgent.status?.slice(1)}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Email</p>
                      <p>{selectedAgent.email?.toLowerCase()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Phone</p>
                      <p>{selectedAgent.phone?.startsWith("+") ? selectedAgent.phone : `+91 ${selectedAgent.phone}`}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500 text-sm">Company</p>
                      <p style={{textTransform: "capitalize"}}>{selectedAgent.company_name?.toLowerCase()}</p>
                    </div>
                    {selectedAgent.gst_number && (
                      <div>
                        <p className="text-gray-500 text-sm">GST Number</p>
                        <p className="uppercase tracking-wide">{selectedAgent.gst_number?.toUpperCase()}</p>
                      </div>
                    )}
                    {selectedAgent.pan_number && (
                      <div>
                        <p className="text-gray-500 text-sm">PAN Number</p>
                        <p className="uppercase tracking-wide">{selectedAgent.pan_number?.toUpperCase()}</p>
                      </div>
                    )}
                    {selectedAgent.website && (
                      <div className="col-span-2">
                        <p className="text-gray-500 text-sm">Website</p>
                        <p>{selectedAgent.website?.toLowerCase()}</p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <p className="text-gray-500 text-sm">Address</p>
                      <p>{selectedAgent.address}, {selectedAgent.city}, {selectedAgent.state}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Registered On</p>
                      <p>{(() => { const d = new Date(selectedAgent.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4">
                    {selectedAgent.status === "pending" && (
                      <Button 
                        onClick={() => approveAgent(selectedAgent.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve
                      </Button>
                    )}
                    {selectedAgent.status !== "blocked" && (
                      <Button 
                        onClick={() => blockAgent(selectedAgent.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Block
                      </Button>
                    )}
                    {selectedAgent.status === "blocked" && (
                      <Button 
                        onClick={() => approveAgent(selectedAgent.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Unblock
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <ImageCropperModal
          open={cropper.open}
          onClose={() => setCropper({ open: false, file: null })}
          onSave={handleCroppedLogoSave}
          file={cropper.file}
          outputSize={512}
          shape="square"
          title="Adjust Company Logo"
        />

        <ImageCropperModal
          open={profilePicCropper.open}
          onClose={() => setProfilePicCropper({ open: false, file: null })}
          onSave={handleCroppedProfilePicSave}
          file={profilePicCropper.file}
          outputSize={512}
          shape="circle"
          title="Adjust Profile Picture"
        />

        {/* Create / Edit Agent Modal */}
        <Dialog open={showAgentFormModal} onOpenChange={setShowAgentFormModal}>
          <DialogContent className="max-w-2xl" data-testid="agent-form-modal">
            <DialogHeader>
              <DialogTitle>{editingAgent ? `Edit Agent — ${editingAgent.name}` : "Create New Agent"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div>
                <Label>Full Name *</Label>
                <Input value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} placeholder="John Doe" data-testid="agent-name-input" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} placeholder="agent@example.com" disabled={!!editingAgent} data-testid="agent-email-input" />
                {editingAgent && <p className="text-xs text-gray-400 mt-1">Email cannot be changed after creation</p>}
              </div>
              <div>
                <Label>Company Name</Label>
                <Input value={agentForm.company_name} onChange={(e) => setAgentForm({ ...agentForm, company_name: e.target.value })} placeholder="Acme Travels Pvt Ltd" data-testid="agent-company-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={agentForm.phone} onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="agent-phone-input" />
              </div>
              <div>
                <Label>GSTIN (leave blank for Non-GST)</Label>
                <Input value={agentForm.gstin} onChange={(e) => setAgentForm({ ...agentForm, gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5" maxLength={15} data-testid="agent-gstin-input" />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input value={agentForm.pan_number} onChange={(e) => setAgentForm({ ...agentForm, pan_number: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} data-testid="agent-pan-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input value={agentForm.address} onChange={(e) => setAgentForm({ ...agentForm, address: e.target.value })} placeholder="Street, Locality" data-testid="agent-address-input" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={agentForm.city} onChange={(e) => setAgentForm({ ...agentForm, city: e.target.value })} data-testid="agent-city-input" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={agentForm.state} onChange={(e) => setAgentForm({ ...agentForm, state: e.target.value })} data-testid="agent-state-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAgentFormModal(false)} disabled={agentFormSaving}>Cancel</Button>
              <Button onClick={saveAgent} disabled={agentFormSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="agent-form-save-btn">
                {agentFormSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {editingAgent ? "Save Changes" : "Create Agent"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default AdminSettings;
